'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { useCommandCenter } from '@/features/command-center/ui/CommandCenterProvider';

interface SearchContextType {
  isOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
}

const SearchContext = createContext<SearchContextType | null>(null);

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within SearchProvider');
  }
  return context;
}

interface SearchProviderProps {
  children: React.ReactNode;
}

export function SearchProvider({ children }: SearchProviderProps) {
  const commandCenter = useCommandCenter();
  const isOpen = commandCenter.isOpen;

  const value = useMemo<SearchContextType>(
    () => ({
      isOpen,
      openSearch: commandCenter.open,
      closeSearch: commandCenter.close,
      toggleSearch: () => {
        if (commandCenter.isOpen) {
          commandCenter.close();
        } else {
          commandCenter.open();
        }
      },
    }),
    [commandCenter, isOpen]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}
